/**
 * wishlist-header-count.js
 *
 * Keeps the header wishlist badge ([data-wishlist-count] in snippets/header-actions.liquid
 * and header-mobile.liquid) in step with the customer's WISHLIST total — the number of
 * saved items that belong to a list, matching the List Manager page.
 *
 * The header heart is a plain link to the wishlist page, not a <swish-drawer>, so the
 * SDK's declarative count binding does not apply. Instead we recount whenever the SDK
 * signals a change.
 *
 * HOW THE COUNT IS DERIVED
 *   refresh() calls listLists() and counts only the items that belong to a list —
 *   identical to what the List Manager page displays. Orphaned profile items (no list
 *   membership) and Save-for-Later items are excluded automatically because they belong
 *   to no list, so they never appear in a list's itemIds/itemCount.
 *
 *   A product can be filed into several lists at once, so when the response carries
 *   per-list `itemIds` we dedupe them through a Set (a product in two lists counts once).
 *   Some SDK responses omit `itemIds` and only carry each list's `itemCount` (see the
 *   same fallback in assets/account-wishlists.js); in that case we sum those counts
 *   instead, so the badge reflects the saved items rather than collapsing to zero.
 *
 * WHEN IT RECOUNTS
 *   We subscribe to the SDK event bus (swish.events) rather than the itemCount() signal,
 *   because the count depends on LIST MEMBERSHIP. Filing an existing item into a list
 *   (the save-to-list picker behind any card/PDP heart) changes membership WITHOUT
 *   creating or deleting an item, so the itemCount() signal never settles — but the
 *   `item-lists-update` event does. Subscribing to the item + list events covers every
 *   path, wherever the change originates (card heart, PDP, the List Manager, the drawer).
 */

import { getSwish, isLoggedIn, listLists } from '@theme/swish-client';

const BADGE_SELECTOR = '[data-wishlist-count]';

/**
 * Every SDK event that can change how many saved items belong to a list:
 * items created/removed, an item re-filed across lists, and lists created/deleted
 * (a deleted list can orphan its items). token-update keeps parity with the previous
 * signal, which refetched on auth refresh.
 */
const COUNT_EVENTS = [
  'item-create',
  'item-delete',
  'item-lists-update',
  'list-create',
  'list-delete',
  'token-update',
];

/** @param {number} count */
function render(count) {
  for (const badge of document.querySelectorAll(BADGE_SELECTOR)) {
    badge.textContent = String(count);
    badge.hidden = count <= 0;
  }
}

/**
 * Number of the customer's saved items that belong to at least one list — the value
 * the List Manager page shows. Prefers a deduped Set of per-list `itemIds` (a product
 * filed into several lists counts once); falls back to summing each list's `itemCount`
 * when the response omits `itemIds`, so the badge is never wrongly zeroed. In both
 * paths, items in no list (orphaned + Save-for-Later) are excluded.
 *
 * @param {Array<{ itemIds?: string[], itemCount?: number }>} lists
 * @returns {number}
 */
function countListedItems(lists) {
  const ids = lists.flatMap((list) => (Array.isArray(list.itemIds) ? list.itemIds : []));
  if (ids.length > 0) return new Set(ids).size;
  return lists.reduce((total, list) => total + (Number(list.itemCount) || 0), 0);
}

// Latest call wins: a slow earlier fetch can't repaint over a newer one.
let sequence = 0;

/** Refetch the customer's lists and repaint the badge. */
function refresh() {
  const ticket = ++sequence;
  listLists()
    .then((lists) => {
      if (ticket !== sequence) return;
      render(countListedItems(lists));
    })
    .catch(() => {
      // List fetch failed: leave the badge in its current state rather than a false 0.
    });
}

async function init() {
  // Guests have nothing saved and the SDK instance is scoped to the customer session.
  if (!isLoggedIn()) return;
  if (!document.querySelector(BADGE_SELECTOR)) return;

  let swish;
  try {
    swish = await getSwish();
  } catch {
    // Embed not loaded on this page: the badge stays hidden.
    return;
  }
  if (typeof swish.events?.subscribe !== 'function') return;

  refresh(); // initial paint
  // Any item/list change repaints the badge, wherever it originates.
  swish.events.subscribe(COUNT_EVENTS, refresh);
}

init();
